import React from "react";
import { CloseCircleFilled, CloseCircleOutlined } from "@ant-design/icons";
import { Spin, Tag } from "antd5";

import { currencyFormatLong } from "components/app_layout/Typography";
import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";

import css from "./QuickFilterBar.module.scss";

// The maxium number of search engine results that can be displayed
const MAX_SEARCH_ENGINE_RESULTS = 1_000_000;

type Props<T> = {
  isLoading: boolean;
  totalResults: number;
  valueSum?: number;
  appliedFilters: (keyof T)[];
  sortField?: string;
  onClear: (key: keyof T) => void;
  filterKeyToLabel: Record<keyof T, string | undefined>;
  dataType: EventDataTypes;
  hiddenFields?: (keyof T)[];
  onFilterClick: (key: keyof T) => void;
  clearAllFilters: () => void;
  clearAllFiltersHide?: () => boolean;
};

export function QuickFilterBar<T>(props: Props<T>) {
  const { totalResults, isLoading, sortField, appliedFilters, hiddenFields, valueSum } = props;

  const totalNumberDisplay = `${totalResults >= MAX_SEARCH_ENGINE_RESULTS ? "1,000,000+" : totalResults}`;
  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);

  const filters = appliedFilters.filter(
    (key) => !hiddenFields?.includes(key) && props.filterKeyToLabel[key],
  );

  return (
    <div className={css.container} aria-label="quick-filter-bar">
      <div className={css.filterBarItem} aria-label="total-results">
        {isLoading ? <Spin size="small" data-testid="quick-filter-loading" /> : totalNumberDisplay}
        <div className={css.filterBarText}>Results</div>
      </div>

      {valueSum !== undefined && (
        <>
          <div className={css.filterBarText}>•</div>
          <div className={css.filterBarItem} aria-label="value-sum">
            <div className={css.filterBarText}>Total Value </div>
            {isLoading ? (
              <Spin size="small" data-testid="quick-filter-value-sum-loading" />
            ) : (
              currencyFormatLong(valueSum ?? 0, "GBP")
            )}
          </div>
        </>
      )}

      {!(isRelevancySearchEnabled && sortField === "Signal score") && (
        <>
          <div className={css.filterBarText}>•</div>
          <div className={css.filterBarItem} aria-label="sorted-by">
            <div className={css.filterBarText}>Sorted by </div>
            {sortField}
          </div>
        </>
      )}

      {filters.length > 0 && (
        <>
          <div className={css.filterBarText}>•</div>
          <FilterChipsContainer<T> {...props} appliedFilters={filters} />
        </>
      )}
    </div>
  );
}
const MAX_NO_OF_FILTERS_TO_DISPLAY = 4;

type FilterChipsContainerProps<T> = Pick<
  Props<T>,
  | "onClear"
  | "filterKeyToLabel"
  | "appliedFilters"
  | "dataType"
  | "onFilterClick"
  | "clearAllFilters"
  | "clearAllFiltersHide"
>;

function FilterChipsContainer<T>({
  appliedFilters,
  onClear,
  filterKeyToLabel,
  dataType,
  onFilterClick,
  clearAllFilters,
  clearAllFiltersHide = () => false,
}: FilterChipsContainerProps<T>) {
  const tracking = useTracking();

  const clearFilter = (key: keyof T) => {
    tracking.logEvent(EventNames.filterActioned, {
      "Context source": "Filter chip",
      "Action type": "Filter cleared",
      "Filter name": filterKeyToLabel[key],
      "Data type": dataType,
    });
    onClear(key);
  };

  return (
    <div aria-label="filter-chips" className={css.filterBarItem}>
      <div className={css.filterBarText}>Filtered by </div>
      <div className={css.filterChipsContainer}>
        {appliedFilters.slice(0, MAX_NO_OF_FILTERS_TO_DISPLAY).map((filterKey) => {
          const label = filterKeyToLabel[filterKey];
          return (
            <Tag
              className={css.filterChip}
              key={filterKey.toString()}
              onClose={() => clearFilter(filterKey)}
              onClick={() => onFilterClick(filterKey)}
              closeIcon={
                <span className={css.closeIconWrapper} aria-label="clear-filter">
                  <CloseCircleOutlined className={`${css.closeIcon} ${css.closeIconDefault}`} />
                  <CloseCircleFilled className={`${css.closeIcon} ${css.closeIconHover}`} />
                </span>
              }
              aria-label={`filter-chip-${filterKey.toString()}`}
            >
              <span className={css.filterChipLabel}>{label}</span>
            </Tag>
          );
        })}
      </div>
      {appliedFilters?.length > MAX_NO_OF_FILTERS_TO_DISPLAY && (
        <CollapsedFilters<T> appliedFilters={appliedFilters} filterKeyToLabel={filterKeyToLabel} />
      )}
      {appliedFilters?.length && !clearAllFiltersHide() && (
        <Button
          buttonType="tertiary"
          onClick={() => {
            clearAllFilters();
          }}
          icon="xCircle"
        >
          Clear filters
        </Button>
      )}
    </div>
  );
}

type CollapsedFiltersProps<T> = Pick<Props<T>, "appliedFilters" | "filterKeyToLabel">;
function CollapsedFilters<T>({ appliedFilters, filterKeyToLabel }: CollapsedFiltersProps<T>) {
  const remainingFilters = appliedFilters.slice(MAX_NO_OF_FILTERS_TO_DISPLAY).map((filterKey) => {
    const label = filterKeyToLabel[filterKey];
    return <li key={filterKey.toString()}>{label}</li>;
  });
  return (
    <Tooltip content={remainingFilters}>
      <span>{`+ ${remainingFilters.length} More`}</span>
    </Tooltip>
  );
}
